import { useMemo, useState } from 'react';

import {
    DataType,
    FrequencyType,
    TaxableIncome,
    TaxableIncomeSaveRequest,
    TaxFilingSaveRequest,
    TaxFilingV2,
    TaxResidencySaveRequest,
    TaxResidencyV2,
} from '@ndvr/portfolio-api';

import { convertFormDateToServerDate } from 'utils/date-formatter';
import {
    useCreateTaxableIncome,
    useCreateTaxFiling,
    useCreateTaxResidency,
    useDeleteTaxableIncome,
    useDeleteTaxFiling,
    useDeleteTaxResidency,
    useTaxableIncomes,
    useTaxFilings,
    useTaxResidencies,
    useUpdateTaxableIncome,
    useUpdateTaxFiling,
    useUpdateTaxResidency,
} from 'views/portfolio/tax-profile-queries';

import { FormValues as IncomeFormValues } from './EditTaxableIncomeDialog';
import { FormValues as FilingFormValues } from './EditTaxFilingDialog';
import { FormValues as ResidencyFormValues } from './EditTaxResidencyDialog';
import { getIncomeStatus } from './tax-profile-utils';

export const useTaxProfile = (accountGroupGUID: string) => {
    const { data: taxableIncomes, isPending: isIncomesLoading } = useTaxableIncomes(accountGroupGUID);
    const { data: taxFilings, isPending: isFilingsLoading } = useTaxFilings(accountGroupGUID);
    const { data: taxResidencies, isPending: isResidenciesLoading } = useTaxResidencies(accountGroupGUID);

    const createIncome = useCreateTaxableIncome(accountGroupGUID);
    const updateIncome = useUpdateTaxableIncome(accountGroupGUID);
    const deleteIncome = useDeleteTaxableIncome(accountGroupGUID);
    const createFiling = useCreateTaxFiling(accountGroupGUID);
    const updateFiling = useUpdateTaxFiling(accountGroupGUID);
    const deleteFiling = useDeleteTaxFiling(accountGroupGUID);
    const createResidency = useCreateTaxResidency(accountGroupGUID);
    const updateResidency = useUpdateTaxResidency(accountGroupGUID);
    const deleteResidency = useDeleteTaxResidency(accountGroupGUID);

    const [editingIncome, setEditingIncome] = useState<TaxableIncome | undefined>(undefined);
    const [isIncomeDialogOpen, setIsIncomeDialogOpen] = useState(false);
    const [incomeToDelete, setIncomeToDelete] = useState<TaxableIncome | undefined>(undefined);

    const [editingFiling, setEditingFiling] = useState<TaxFilingV2 | undefined>(undefined);
    const [isFilingDialogOpen, setIsFilingDialogOpen] = useState(false);
    const [filingToDelete, setFilingToDelete] = useState<TaxFilingV2 | undefined>(undefined);

    const [editingResidency, setEditingResidency] = useState<TaxResidencyV2 | undefined>(undefined);
    const [isResidencyDialogOpen, setIsResidencyDialogOpen] = useState(false);
    const [residencyToDelete, setResidencyToDelete] = useState<TaxResidencyV2 | undefined>(undefined);

    const groupedTaxableIncome = useMemo(
        () => ({
            active:
                taxableIncomes
                    ?.filter((income) => getIncomeStatus(income) === 'active')
                    .sort(
                        (a, b) =>
                            new Date(a.factorDateFrame.startDate).getTime() -
                            new Date(b.factorDateFrame.startDate).getTime(),
                    ) || [],
            upcoming:
                taxableIncomes
                    ?.filter((income) => getIncomeStatus(income) === 'upcoming')
                    .sort(
                        (a, b) =>
                            new Date(a.factorDateFrame.startDate).getTime() -
                            new Date(b.factorDateFrame.startDate).getTime(),
                    ) || [],
            expired:
                taxableIncomes
                    ?.filter((income) => getIncomeStatus(income) === 'expired')
                    .sort(
                        (a, b) =>
                            new Date(b.factorDateFrame.startDate).getTime() -
                            new Date(a.factorDateFrame.startDate).getTime(),
                    ) || [],
        }),
        [taxableIncomes],
    );

    const sortedFilings = useMemo(
        () =>
            taxFilings
                ?.slice()
                .sort((a, b) => new Date(b.effectiveFrom).getTime() - new Date(a.effectiveFrom).getTime()) || [],
        [taxFilings],
    );

    const mostRecentActiveFiling = useMemo(() => {
        return sortedFilings.find((filing) => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const effectiveDate = new Date(filing.effectiveFrom);
            effectiveDate.setHours(0, 0, 0, 0);
            return effectiveDate <= today;
        });
    }, [sortedFilings]);

    const groupedTaxFilings = useMemo(
        () => ({
            active: sortedFilings.filter((filing) => filing === mostRecentActiveFiling) || [],
            upcoming:
                sortedFilings
                    .filter((filing) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const effectiveDate = new Date(filing.effectiveFrom);
                        effectiveDate.setHours(0, 0, 0, 0);
                        return effectiveDate > today;
                    })
                    .sort((a, b) => new Date(a.effectiveFrom).getTime() - new Date(b.effectiveFrom).getTime()) || [],
            expired:
                sortedFilings.filter((filing) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const effectiveDate = new Date(filing.effectiveFrom);
                    effectiveDate.setHours(0, 0, 0, 0);
                    return effectiveDate <= today && filing !== mostRecentActiveFiling;
                }) || [],
        }),
        [sortedFilings, mostRecentActiveFiling],
    );

    const sortedResidencies = useMemo(
        () =>
            taxResidencies
                ?.slice()
                .sort((a, b) => new Date(b.effectiveFrom).getTime() - new Date(a.effectiveFrom).getTime()) || [],
        [taxResidencies],
    );

    const mostRecentActiveResidency = useMemo(() => {
        return sortedResidencies.find((residency) => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const effectiveDate = new Date(residency.effectiveFrom);
            effectiveDate.setHours(0, 0, 0, 0);
            return effectiveDate <= today;
        });
    }, [sortedResidencies]);

    const groupedTaxResidencies = useMemo(
        () => ({
            active: sortedResidencies.filter((residency) => residency === mostRecentActiveResidency) || [],
            upcoming:
                sortedResidencies
                    .filter((residency) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const effectiveDate = new Date(residency.effectiveFrom);
                        effectiveDate.setHours(0, 0, 0, 0);
                        return effectiveDate > today;
                    })
                    .sort((a, b) => new Date(a.effectiveFrom).getTime() - new Date(b.effectiveFrom).getTime()) || [],
            expired:
                sortedResidencies.filter((residency) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const effectiveDate = new Date(residency.effectiveFrom);
                    effectiveDate.setHours(0, 0, 0, 0);
                    return effectiveDate <= today && residency !== mostRecentActiveResidency;
                }) || [],
        }),
        [sortedResidencies, mostRecentActiveResidency],
    );

    const handleAddIncome = () => {
        setEditingIncome(undefined);
        setIsIncomeDialogOpen(true);
    };

    const handleEditIncome = (income: TaxableIncome, isActive: boolean = false) => {
        setEditingIncome(income);
        setIsIncomeDialogOpen(true);
    };

    const handleSaveIncome = async (data: IncomeFormValues) => {
        const request: TaxableIncomeSaveRequest = {
            title: {
                encryptionContextGUID: accountGroupGUID,
                dataType: DataType.SI_FACTOR_TITLE,
                plainText: data.title,
            },
            taxableIncomeType: data.taxableIncomeType,
            amount: String(data.amount),
            factorDateFrame: {
                startDate: convertFormDateToServerDate(data.startDate),
                endDate:
                    data.repeatIncome && data.repeatUntil === 'specific-date' && data.endDate
                        ? convertFormDateToServerDate(data.endDate)
                        : undefined,
                frequencyType: data.repeatIncome && data.frequencyType ? data.frequencyType : FrequencyType.ONE_TIME,
                occurrenceCount:
                    data.repeatIncome && data.repeatUntil === 'occurrences' ? data.occurrenceCount : undefined,
            },
            inflationConfig: {
                inflationAdjusted: data.inflationAdjusted || false,
                inflationPercent: data.inflationPercent ? String(data.inflationPercent) : undefined,
                inflationStartDate: data.inflationAdjusted ? convertFormDateToServerDate(data.startDate) : undefined,
            },
        };

        if (editingIncome) {
            await updateIncome.mutateAsync({ guid: editingIncome.taxableIncomeGUID, request });
        } else {
            await createIncome.mutateAsync(request);
        }

        setIsIncomeDialogOpen(false);
        setEditingIncome(undefined);
    };

    const handleCancelIncomeDialog = () => {
        setIsIncomeDialogOpen(false);
        setEditingIncome(undefined);
    };

    const handleDeleteIncome = (e: React.MouseEvent, income: TaxableIncome) => {
        e.stopPropagation();
        setIncomeToDelete(income);
    };

    const confirmDeleteIncome = async () => {
        if (incomeToDelete) {
            await deleteIncome.mutateAsync(incomeToDelete.taxableIncomeGUID);
            setIncomeToDelete(undefined);
        }
    };

    const handleAddFiling = () => {
        setEditingFiling(undefined);
        setIsFilingDialogOpen(true);
    };

    const handleEditFiling = (filing: TaxFilingV2, isActive: boolean = false) => {
        setEditingFiling(filing);
        setIsFilingDialogOpen(true);
    };

    const handleSaveFiling = async (data: FilingFormValues) => {
        const request: TaxFilingSaveRequest = {
            title: {
                encryptionContextGUID: accountGroupGUID,
                dataType: DataType.SI_FACTOR_TITLE,
                plainText: data.title,
            },
            taxFilingStatus: data.taxFilingStatus,
            effectiveFrom: convertFormDateToServerDate(data.effectiveFrom),
        };

        if (editingFiling) {
            await updateFiling.mutateAsync({ guid: editingFiling.taxFilingGUID, request });
        } else {
            await createFiling.mutateAsync(request);
        }

        setIsFilingDialogOpen(false);
        setEditingFiling(undefined);
    };

    const handleCancelFilingDialog = () => {
        setIsFilingDialogOpen(false);
        setEditingFiling(undefined);
    };

    const handleDeleteFiling = (e: React.MouseEvent, filing: TaxFilingV2) => {
        e.stopPropagation();
        setFilingToDelete(filing);
    };

    const confirmDeleteFiling = async () => {
        if (filingToDelete) {
            await deleteFiling.mutateAsync(filingToDelete.taxFilingGUID);
            setFilingToDelete(undefined);
        }
    };

    const handleAddResidency = () => {
        setEditingResidency(undefined);
        setIsResidencyDialogOpen(true);
    };

    const handleEditResidency = (residency: TaxResidencyV2, isActive: boolean = false) => {
        setEditingResidency(residency);
        setIsResidencyDialogOpen(true);
    };

    const handleSaveResidency = async (data: ResidencyFormValues) => {
        const request: TaxResidencySaveRequest = {
            title: {
                encryptionContextGUID: accountGroupGUID,
                dataType: DataType.SI_FACTOR_TITLE,
                plainText: data.title,
            },
            postalCode: data.postalCode,
            effectiveFrom: convertFormDateToServerDate(data.effectiveFrom),
        };

        if (editingResidency) {
            await updateResidency.mutateAsync({ guid: editingResidency.taxResidencyGUID, request });
        } else {
            await createResidency.mutateAsync(request);
        }

        setIsResidencyDialogOpen(false);
        setEditingResidency(undefined);
    };

    const handleCancelResidencyDialog = () => {
        setIsResidencyDialogOpen(false);
        setEditingResidency(undefined);
    };

    const handleDeleteResidency = (e: React.MouseEvent, residency: TaxResidencyV2) => {
        e.stopPropagation();
        setResidencyToDelete(residency);
    };

    const confirmDeleteResidency = async () => {
        if (residencyToDelete) {
            await deleteResidency.mutateAsync(residencyToDelete.taxResidencyGUID);
            setResidencyToDelete(undefined);
        }
    };

    const canDeleteActiveIncome = (income: TaxableIncome): boolean => {
        return true;
    };

    const canDeleteActiveFiling = (filing: TaxFilingV2): boolean => {
        if (!groupedTaxFilings.active.includes(filing)) return true;
        const allPastFilings = sortedFilings.filter((f) => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const effectiveDate = new Date(f.effectiveFrom);
            effectiveDate.setHours(0, 0, 0, 0);
            return effectiveDate <= today;
        });
        return allPastFilings.length > 1;
    };

    const canDeleteActiveResidency = (residency: TaxResidencyV2): boolean => {
        if (!groupedTaxResidencies.active.includes(residency)) return true;
        const allPastResidencies = sortedResidencies.filter((r) => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const effectiveDate = new Date(r.effectiveFrom);
            effectiveDate.setHours(0, 0, 0, 0);
            return effectiveDate <= today;
        });
        return allPastResidencies.length > 1;
    };

    return {
        groupedTaxableIncome,
        groupedTaxFilings,
        groupedTaxResidencies,
        isLoading: isIncomesLoading || isFilingsLoading || isResidenciesLoading,
        editingIncome,
        isIncomeDialogOpen,
        incomeToDelete,
        setIncomeToDelete,
        editingFiling,
        isFilingDialogOpen,
        filingToDelete,
        setFilingToDelete,
        editingResidency,
        isResidencyDialogOpen,
        residencyToDelete,
        setResidencyToDelete,
        handleAddIncome,
        handleEditIncome,
        handleSaveIncome,
        handleCancelIncomeDialog,
        handleDeleteIncome,
        confirmDeleteIncome,
        handleAddFiling,
        handleEditFiling,
        handleSaveFiling,
        handleCancelFilingDialog,
        handleDeleteFiling,
        confirmDeleteFiling,
        handleAddResidency,
        handleEditResidency,
        handleSaveResidency,
        handleCancelResidencyDialog,
        handleDeleteResidency,
        confirmDeleteResidency,
        canDeleteActiveIncome,
        canDeleteActiveFiling,
        canDeleteActiveResidency,
        deleteIncome,
        deleteFiling,
        deleteResidency,
    };
};
